export const ROW_WARNING_FILL = '#FFFAEB';
export const ROW_WARNING_EDGE = '#DC6803';

export function isMissingEffectiveValue(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

export function rowLacksSoldOrBought(input: {
  sold?: { columnKey: string | null; missing: boolean };
  bought?: { columnKey: string | null; missing: boolean };
}): boolean {
  if (input.sold?.missing || input.bought?.missing) return true;
  if (!input.sold || !input.bought) return false;
  return !input.sold.columnKey && !input.bought.columnKey;
}

export function warningState(input: { soldMissing: boolean; boughtMissing: boolean }): {
  rowWarning: boolean;
  soldCellWarning: boolean;
  boughtCellWarning: boolean;
} {
  return {
    rowWarning: input.soldMissing || input.boughtMissing,
    soldCellWarning: input.soldMissing,
    boughtCellWarning: input.boughtMissing,
  };
}

export function orderByWarningThenEveId<T extends { eveId: number; warning: boolean }>(rows: readonly T[]): T[] {
  return [...rows].sort((left, right) => {
    if (left.warning !== right.warning) return left.warning ? -1 : 1;
    return left.eveId - right.eveId;
  });
}

/** Search, page, and row-data refetch regroup warnings. In-place warning flags do not. */
export function warningRequeryKey(input: {
  searchQuery: string;
  page: number;
  pageSize: number;
  variantIds: readonly string[];
  rowsUpdatedAt: number;
  catalogUpdatedAt: number;
}): string {
  return [
    input.searchQuery,
    input.page,
    input.pageSize,
    input.variantIds.join(','),
    input.rowsUpdatedAt,
    input.catalogUpdatedAt,
  ].join('|');
}

export function holdPositionsUntilRequery<T extends { id: string; eveId: number; warning: boolean }>(
  previousIds: readonly string[] | null,
  rows: readonly T[],
  requery: boolean,
): T[] {
  if (requery || previousIds == null) return orderByWarningThenEveId(rows);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const kept: T[] = [];
  for (const id of previousIds) {
    const row = byId.get(id);
    if (row) kept.push(row);
  }
  const known = new Set(previousIds);
  return [...kept, ...orderByWarningThenEveId(rows.filter((row) => !known.has(row.id)))];
}

/**
 * The first committed order is the page load. Do not commit it until Sold As /
 * Bought As defaults and overrides are known — including the first-time defaults
 * written for an existing organisation. A later warning change keeps position
 * until the next re-query.
 */
export function resolveWarningOrder<T extends { id: string; eveId: number; warning: boolean }>(input: {
  ready: boolean;
  previousIds: readonly string[] | null;
  rows: readonly T[];
  requery: boolean;
}): { rows: T[]; commit: boolean } {
  if (!input.ready) {
    if (input.previousIds == null) return { rows: [...input.rows], commit: false };
    return {
      rows: holdPositionsUntilRequery(input.previousIds, input.rows, false),
      commit: false,
    };
  }
  return {
    rows: holdPositionsUntilRequery(input.previousIds, input.rows, input.requery),
    commit: true,
  };
}
